/* eslint-disable max-len */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Field, useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { RadioButton } from 'primereact/radiobutton';
import {
  InputTextWithValidationMessage,
  DropdownWithValidation,
  TextAreaWithValidation,
  CommissionPercentageInput,
  FormikMultiSelect,
} from '../../../components/prime-formik';
import { AccountContext } from '../../../components/AccountContext';
import {
  AccountType,
  MAX_CREDIT_COMMISSION_PERCENTAGE,
  MAX_NO_LIMIT_COMMISSION_PERCENTAGE,
  MINIMUM_COMMISSION_PERCENTAGE,
  MINIMUM_CREDIT_COMMISSION_PERCENTAGE,
  NumberOfEmployees,
  PayedBy,
} from '../../../shared/Constants';
import useGpMessageToOwner from '../../../hooks/useGpMessageToOwner';
import { getNumberOfEmployeesLabel } from '../../../utils/AppUtil';

const messages = defineMessages({
  panelTitle: { id: 'accounts.add-client.panel-header', defaultMessage: 'Add Client account' },
  fullName: { id: 'attrs.owner.fullName', defaultMessage: "Owner's full name" },
  email: { id: 'attrs.owner.email', defaultMessage: "Owner's e-mail" },
  plan: { id: 'attrs.credit-plan', defaultMessage: 'Account Credit Plan' },
  preferredLanguage: { id: 'attrs.preferred.language', defaultMessage: 'Preferred language' },
  ownerInvoiceCountry: { id: 'attrs.owner-invoice-country', defaultMessage: "Owner's invoice country" },
  ownerBusinessCountries: { id: 'attrs.owner-business-countries', defaultMessage: "Owner's business countries" },
  message: { id: 'attrs.owner.message', defaultMessage: 'Message to Owner: ' },
  clientDetailsStepName: { id: 'accounts.add-client.steps.client-details', defaultMessage: 'Client' },
  assessmentStepName: { id: 'accounts.add-client.steps.assessment', defaultMessage: 'Assessment' },
  surveyStepName: { id: 'accounts.add-client.steps.survey', defaultMessage: 'Survey' },
  reviewStepName: { id: 'accounts.add-client.steps.review', defaultMessage: 'Review' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  available: { id: 'attrs.available', defaultMessage: 'Available' },
  selected: { id: 'attrs.selected', defaultMessage: 'Selected' },
  interestsCheckbox: {
    id: 'accounts.add-client.assessment-dimensions.interests-text',
    defaultMessage: 'Give access to the Interests section',
  },
  payedByClient: {
    id: 'accounts.add-client.payed-by-client',
    defaultMessage: "GPI invoices the client",
  },
  payedByParent: {
    id: 'accounts.add-client.payed-by-parent',
    defaultMessage: "GPI invoices me and I invoice the client",
  },
  freeTrial: {
    id: 'accounts.add-client.free-trial',
    defaultMessage: 'Free trial',
  },
  numberOfInternalRespondents: {
    id: 'accounts.add-client.number-of-internal-respondents',
    defaultMessage: 'Number of Internal Respondents',
  },
  selectedByClient: {
    id: 'accounts.add-client.selected-by-client',
    defaultMessage: 'Selected by client',
  },
  commissionOnCredits: {
    id: 'accounts.add-client.commission-on-credits',
    defaultMessage: '% commission on Credits',
  },
  commissionOnNoLimit: {
    id: 'accounts.add-client.commission-on-no-limit',
    defaultMessage: '% commission on NoLimit',
  },
  commissionSuperMatchCredits: {
    id: 'accounts.add-client.commission-on-super-match-credits',
    defaultMessage: '% commission on SuperMatch Credits',
  },
  no: { id: 'btn.lbl.no', defaultMessage: 'No' },
  month: {
    id: 'accounts.add-client.free-trial-options.month',
    defaultMessage: 'month',
  },
  months: {
    id: 'accounts.add-client.free-trial-options.months',
    defaultMessage: 'months',
  },
});

const ClientStep = ({
  isCurrentStep,
  languages,
  countries,
  account,
  type,
  availableNumberOfEmployees,
  currentAccountType,
}) => {
  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);

  const { buildMessageToOwner } = useGpMessageToOwner();

  const {
    cldr: { localeDisplayName },
  } = useSelector((state) => state.i18n);

  const {
    values: {
      preferredLanguage,
      payedBy,
      creditCommissionPercentage,
      noLimitCommissionPercentage,
    },
    setFieldValue,
  } = useFormikContext();

  const languageOptions = languages.map((locale) => ({
    value: locale,
    label: localeDisplayName(locale),
  }));

  const buildNumberOfEmployeesOptions = () => {
    const numberOfEmployeesOptions = [];
    numberOfEmployeesOptions.push({
      value: NumberOfEmployees.SELECTED_BY_CLIENT,
      label: formatMessage(messages.selectedByClient),
    });

    availableNumberOfEmployees.forEach((numberOfEmployees) => {
      const option = {
        value: numberOfEmployees,
        label: getNumberOfEmployeesLabel(numberOfEmployees),
      };

      numberOfEmployeesOptions.push(option);
    });

    return numberOfEmployeesOptions;
  };

  const numberOfEmployeesOptions = buildNumberOfEmployeesOptions();
  const allFreeTrialMonthsOptions = [
    {
      value: 0,
      label: formatMessage(messages.no),
    },
    {
      value: 1,
      label: `1 ${formatMessage(messages.month)}`,
    },
    {
      value: 2,
      label: `2 ${formatMessage(messages.months)}`,
    },
    {
      value: 3,
      label: `3 ${formatMessage(messages.months)}`,
    },
  ];

  const freeTrialMonthsOptions =
    payedBy === PayedBy.PAYED_BY_SELF
      ? allFreeTrialMonthsOptions
      : allFreeTrialMonthsOptions.filter((option) => option.value === 0 || option.value === 1);

  if (!isCurrentStep) {
    return null;
  }

  const gpiIsCreatingAnInvitationForGsc = currentAccountType === AccountType.GPI && type === AccountType.GSC;
  const gscIsCreatingAnInvitationForClient = currentAccountType === AccountType.GSC && type === AccountType.NBM_CLIENT;
  const invitationIsForGp = type === AccountType.GP;
  const gpIsCreatingInvitationForClient = currentAccountType === AccountType.GP && type === AccountType.NBM_CLIENT;

  return (
    <div className="p-grid p-fluid">
      {gscIsCreatingAnInvitationForClient && (
        <>
          <div className="p-col-12">
            <Field
              id="payedByClient"
              name="payedBy"
              component={RadioButton}
              onChange={(e) => {
                setFieldValue('payedBy', e.value);
              }}
              checked={payedBy === PayedBy.PAYED_BY_SELF}
              value={PayedBy.PAYED_BY_SELF}
            />
            <label htmlFor="payedByClient">{formatMessage(messages.payedByClient)}</label>
          </div>
          <div className="p-col-12">
            <Field
              id="payedByGPI"
              name="payedBy"
              component={RadioButton}
              onChange={(e) => {
                setFieldValue('payedBy', e.value);
              }}
              checked={payedBy === PayedBy.PAYED_BY_PARENT}
              value={PayedBy.PAYED_BY_PARENT}
            />
            <label htmlFor="payedByGPI">{formatMessage(messages.payedByParent)}</label>
          </div>
        </>
      )}
      {invitationIsForGp && (
        <>
          <div className="p-col-12 commission-percentage-container">
            <Field
              id="creditCommissionPercentage"
              name="creditCommissionPercentage"
              type="number"
              min={MINIMUM_CREDIT_COMMISSION_PERCENTAGE}
              max={MAX_CREDIT_COMMISSION_PERCENTAGE}
              component={CommissionPercentageInput}
              onChange={(e) => {
                setFieldValue('creditCommissionPercentage', e.target.value);

                buildMessageToOwner(
                  preferredLanguage,
                  e.target.value,
                  noLimitCommissionPercentage,
                  (messageToOwner) => setFieldValue('inviteMessage', messageToOwner),
                );
              }}
            />
            <div className="commission-label">{formatMessage(messages.commissionOnCredits)}</div>
          </div>
          <div className="p-col-12 commission-percentage-container">
            <Field
              id="noLimitCommissionPercentage"
              name="noLimitCommissionPercentage"
              type="number"
              min={MINIMUM_COMMISSION_PERCENTAGE}
              max={MAX_NO_LIMIT_COMMISSION_PERCENTAGE}
              component={CommissionPercentageInput}
              onChange={(e) => {
                setFieldValue('noLimitCommissionPercentage', e.target.value);

                buildMessageToOwner(
                  preferredLanguage,
                  creditCommissionPercentage,
                  e.target.value,
                  (messageToOwner) => setFieldValue('inviteMessage', messageToOwner),
                );
              }}
            />
            <div className="commission-label">{formatMessage(messages.commissionOnNoLimit)}</div>
          </div>
        </>
      )}
      {(gscIsCreatingAnInvitationForClient || invitationIsForGp || gpIsCreatingInvitationForClient) && (
        <div className="p-col-12">
          <label htmlFor="freeTrialMonths">{formatMessage(messages.freeTrial)}</label>
          <Field
            id="freeTrialMonths"
            name="freeTrialMonths"
            labelText={formatMessage(messages.freeTrial)}
            component={DropdownWithValidation}
            options={freeTrialMonthsOptions}
            onChange={(e) => {
              setFieldValue('freeTrialMonths', e.target.value);
            }}
          />
        </div>
      )}
      {(gscIsCreatingAnInvitationForClient || gpIsCreatingInvitationForClient) && payedBy === PayedBy.PAYED_BY_SELF && (
        <div className="p-col-12">
          <label htmlFor="numberOfEmployees">{formatMessage(messages.numberOfInternalRespondents)}</label>
          <Field
            id="numberOfEmployees"
            name="numberOfEmployees"
            labelText={formatMessage(messages.numberOfInternalRespondents)}
            component={DropdownWithValidation}
            options={numberOfEmployeesOptions}
            onChange={(e) => {
              setFieldValue('numberOfEmployees', e.target.value);
            }}
          />
        </div>
      )}
      <div className="p-col-12">
        <Field
          id="fullName"
          name="fullName"
          labelText={formatMessage(messages.fullName)}
          component={InputTextWithValidationMessage}
        />
      </div>
      <div className="p-col-12">
        <Field
          id="email"
          name="email"
          type="email"
          labelText={formatMessage(messages.email)}
          component={InputTextWithValidationMessage}
        />
      </div>
      <div className="p-col-12">
        <label htmlFor="preferredLanguage">{formatMessage(messages.preferredLanguage)}</label>
        <Field
          id="preferredLanguage"
          name="preferredLanguage"
          labelText={formatMessage(messages.preferredLanguage)}
          component={DropdownWithValidation}
          filter
          filterBy="label"
          options={languageOptions}
          onChange={(e) => {
            setFieldValue('preferredLanguage', e.target.value);
            if (invitationIsForGp) {
              buildMessageToOwner(
                e.target.value,
                noLimitCommissionPercentage,
                (messageToOwner) => setFieldValue('inviteMessage', messageToOwner),
              );
            }
          }}
        />
      </div>
      {(gpiIsCreatingAnInvitationForGsc || invitationIsForGp) && (
        <div className="p-col-12">
          <label htmlFor="ownerInvoiceCountry">{formatMessage(messages.ownerInvoiceCountry)}</label>
          <Field
            id="ownerInvoiceCountry"
            name="ownerInvoiceCountry"
            labelText={formatMessage(messages.ownerInvoiceCountry)}
            component={DropdownWithValidation}
            filter
            filterBy="label"
            options={countries}
          />
        </div>
      )}
      {gpiIsCreatingAnInvitationForGsc && (
        <div className="p-col-12">
          <label htmlFor="ownerBusinessCountries">{formatMessage(messages.ownerBusinessCountries)}</label>

          <Field
            setFieldValue={setFieldValue}
            id="ownerBusinessCountries"
            name="ownerBusinessCountries"
            labelText={formatMessage(messages.ownerBusinessCountries)}
            component={FormikMultiSelect}
            filter
            filterBy="label"
            options={countries}
          />
        </div>
      )}
      <div className="p-col-12">
        <label htmlFor="inviteMessage">{formatMessage(messages.message)}</label>
        <Field id="inviteMessage" name="inviteMessage" component={TextAreaWithValidation} rows={6} />
      </div>
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.steps.client.details"
          defaultMessage="The new account will be created under {partnerName}"
          values={{
            partnerName: <strong>{account.displayName}</strong>,
          }}
          tagName="p"
        />
        <input type="hidden" name="partnerParentId" value={currentAccount} />
        <input type="hidden" name="type" value={type} />
      </div>
    </div>
  );
};

ClientStep.propTypes = {
  isCurrentStep: PropTypes.bool.isRequired,
  account: PropTypes.objectOf(PropTypes.any).isRequired,
  type: PropTypes.string.isRequired,
  currentAccountType: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(PropTypes.any).isRequired,
  countries: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default ClientStep;
